/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-07-24
 */

// Add third-party dependencies.
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import { CheckCircle, Error } from '@mui/icons-material';
import { useParams, useNavigate, Link } from 'react-router-dom';

// Add local dependencies.
import { useEmailVerificationConfirmMutation } from '../../../features/auth';

/**
 * Email verification confirmation component.
 * @example
 * return (
 *   <EmailVerificationConfirm />
 * )
 * @returns {React.ReactElement} component.
 */
const EmailVerificationConfirm: React.FC = () => {
  const { t } = useTranslation('auth');
  // Get token from URL params
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  // Component state
  const [verificationStatus, setVerificationStatus] = useState<
    'loading' | 'success' | 'error'
  >('loading');
  const [errorMessage, setErrorMessage] = useState('');

  // API hooks
  const [confirmVerification] = useEmailVerificationConfirmMutation();

  // Verify email on component mount
  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setVerificationStatus('error');
        setErrorMessage(
          'Invalid verification link. Please check your email and try again.'
        );
        return;
      }

      try {
        await confirmVerification({ token }).unwrap();
        setVerificationStatus('success');

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login', {
            state: {
              message: 'Email verified successfully! You can now sign in.',
            },
          });
        }, 3000);
      } catch (error: any) {
        setVerificationStatus('error');
        setErrorMessage(
          error.data?.message ||
            'Verification failed. The link may be expired or invalid.'
        );
      }
    };

    verifyEmail();
  }, [token, confirmVerification, navigate]);

  // Render loading state
  if (verificationStatus === 'loading') {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            marginBottom: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper
            elevation={3}
            sx={{
              padding: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              textAlign: 'center',
            }}
          >
            <CircularProgress size={64} sx={{ mb: 3 }} />
            <Typography component="h1" variant="h4" gutterBottom>
              {t('emailVerification.confirm.loading.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('emailVerification.confirm.loading.message')}
            </Typography>
          </Paper>
        </Box>
      </Container>
    );
  }

  // Render success state
  if (verificationStatus === 'success') {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            marginBottom: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper
            elevation={3}
            sx={{
              padding: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              textAlign: 'center',
            }}
          >
            <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 3 }} />
            <Typography component="h1" variant="h4" gutterBottom>
              {t('emailVerification.confirm.success.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {t('emailVerification.confirm.success.message')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('emailVerification.confirm.success.redirect')}
            </Typography>
          </Paper>
        </Box>
      </Container>
    );
  }

  // Render error state
  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          marginBottom: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            textAlign: 'center',
          }}
        >
          <Error sx={{ fontSize: 64, color: 'error.main', mb: 3 }} />
          <Typography component="h1" variant="h4" gutterBottom>
            {t('emailVerification.confirm.error.title')}
          </Typography>

          <Alert severity="error" sx={{ mb: 3, width: '100%' }}>
            {errorMessage}
          </Alert>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('emailVerification.confirm.error.description')}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            <Button
              variant="contained"
              color="primary"
              component={Link}
              to="/register"
            >
              {t('emailVerification.confirm.buttons.registerAgain')}
            </Button>
            <Button variant="outlined" component={Link} to="/login">
              {t('emailVerification.confirm.buttons.signIn')}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default EmailVerificationConfirm;
